import emitAsync from '../utils/emitAsync';
import { useDateStore } from '../stores/useDateStore';
import { useOperationalMapStore } from '../stores/useOperationalMapStore';
import { useTripsStore } from '../stores/useTripsStore';
import { useLinesStore, getLinhasSelecionadasEfetivas } from '../stores/useLinesStore';
import { useClientsStore } from '../stores/useClientsStore';
import { useAuthStore } from '../stores/useAuthStore';
import { useLoadingStore } from '../stores/useLoadingStore';
import { getTripKey } from '../utils/tripAnalysis';

const loadingScopes = new Set();
const loadingRequests = new Map();

const { 
  setLoadingTrips,
  setLoadingFooter,
  setLoadingFooterTxt  
} = useLoadingStore.getState();

function finishTripsWithoutRequest() {

  const { loadingLines } = useLoadingStore.getState();

  if (
    loadingLines ||
    loadingRequests.size
  ) {
    return;
  }

  setLoadingTrips(false);
  setLoadingFooter(false);
  setLoadingFooterTxt('Carregando...');
}

export function isTripsRequestActive(requestId) {

  if (!requestId) {
    return false;
  }

  return loadingRequests.has(requestId);

}

export function finishTripsRequest(requestId) {

    if (!requestId) {
        return;
    }

    const scopeKeys =
        loadingRequests.get(requestId);

    if (!scopeKeys) {
        return;
    }

    for (const scopeKey of scopeKeys) {
        loadingScopes.delete(scopeKey);
    }

    loadingRequests.delete(requestId);

    if (!loadingRequests.size) {

      setLoadingTrips(false);
      setLoadingFooter(false);
      setLoadingFooterTxt('Carregando...');

    }

}

export function clearTripsRequests() {

  loadingScopes.clear();
  loadingRequests.clear();
  setLoadingTrips(false);
  setLoadingFooter(false);
  setLoadingFooterTxt('Carregando...');

}

export async function loadTrips(socket) {

  const date = useDateStore.getState().today;

  const {
    isOperationalMapMode,
    operationalMapDateRange
  } = useOperationalMapStore.getState();

  if (isOperationalMapMode) {

    if (
      !operationalMapDateRange?.startDate ||
      !operationalMapDateRange?.endDate
    ) {

      finishTripsWithoutRequest();

      return false;

    }

    return loadOperationalMapTrips(
      socket,
      operationalMapDateRange
    );
  }

  if (!date) {

    finishTripsWithoutRequest();

    return false;

  }

  const linesState = useLinesStore.getState();

  const tripsState = useTripsStore.getState();

  const codigosJaCarregados = tripsState.getLoadedCodesByDate(date);

  const linhasSelecionadas =
    getLinhasSelecionadasEfetivas({
        linhasDisponiveis:
            linesState.linhasDisponiveis,

        linhasSelecionadas:
            linesState.linhasSelecionadas,

        allowClientes:
            useClientsStore.getState().allowClientes,

        allowPerfis:
            useAuthStore.getState().allowPerfis,

        sentidosSet:
            linesState.sentidosSet
    });

  const linhasParaCarregar =
    linhasSelecionadas.filter(codigo => {
      
      const codigoStr = String(codigo);

      if (codigosJaCarregados.has(codigoStr)) {
        return false;
      }

      if (loadingScopes.has(getTripKey(date, codigoStr))) {
        return false;
      }

      return true;

    });

  if (!linhasParaCarregar.length) {

    finishTripsWithoutRequest();

    return true;

  }

  const scopeKeys =
    linhasParaCarregar.map(
      codigo =>
        getTripKey(
          date,
          String(codigo)
        )
    );

  const requestId = crypto.randomUUID();

  for (const scopeKey of scopeKeys) {
    loadingScopes.add(scopeKey);
  }

  loadingRequests.set(
    requestId,
    scopeKeys
  );

  try {

    setLoadingTrips(true);
    setLoadingFooter(true);
    setLoadingFooterTxt('Carregando viagens...');

    const response = await emitAsync(
      socket,
      'tripsByDate:get',
      {
        requestId,
        date: date || null,
        linhasParaCarregar
      }
    );

    if (!response?.ok) {

      finishTripsRequest(requestId);

      return false;
    }

    return true;
    
  } catch (err) {

    finishTripsRequest(requestId);

    throw err;

  }
  
}

async function loadOperationalMapTrips(
  socket,
  operationalMapDateRange
) {

  const {
    startDate,
    endDate
  } = operationalMapDateRange;

  const linesState = useLinesStore.getState();

  const linhasSelecionadas =
    getLinhasSelecionadasEfetivas({
      linhasDisponiveis:
        linesState.linhasDisponiveis,

      linhasSelecionadas:
        linesState.linhasSelecionadas,

      allowClientes:
        useClientsStore.getState().allowClientes,

      allowPerfis:
        useAuthStore.getState().allowPerfis,

      sentidosSet:
        linesState.sentidosSet
    });

  if (!linhasSelecionadas.length) {

    finishTripsWithoutRequest();

    return true;
  }

  const start = startDate.format('YYYY-MM-DD');

  const end = endDate.format('YYYY-MM-DD');

  const date = useDateStore.getState().today;

  let linhasParaCarregar =
    linhasSelecionadas.map(
      codigo =>
        String(codigo)
    );

  // =========================
  // MESMO DIA DA TORRE
  // =========================

  if ( start === date && end === date) {

    const tripsState = useTripsStore.getState();

    const codigosJaCarregados =
      tripsState.getLoadedCodesByDate(
        date
      );

    linhasParaCarregar =
      linhasParaCarregar.filter(
        codigo =>
          !codigosJaCarregados.has(
            codigo
          )
      );

    if (!linhasParaCarregar.length) {

      console.log('✅ Viagens do período já carregadas pela Torre');

      finishTripsWithoutRequest();

      return true;
    }
  }

  // =========================
  // REMOVE SCOPES JÁ CARREGANDO
  // =========================

  linhasParaCarregar =
    linhasParaCarregar.filter(
      codigo => {

        const scopeKey =
          `operational:${start}:${end}:${codigo}`;

        return !loadingScopes.has(
          scopeKey
        );
      }
    );

  if (!linhasParaCarregar.length) {

    finishTripsWithoutRequest();

    return true;
  }

  // =========================
  // CRIA SCOPES
  // =========================

  const scopeKeys =
    linhasParaCarregar.map(
      codigo =>
        `operational:${start}:${end}:${codigo}`
    );

  const requestId = crypto.randomUUID();

  for (const scopeKey of scopeKeys) {

    loadingScopes.add(scopeKey);
  }

  loadingRequests.set(
    requestId,
    scopeKeys
  );

  try {

    setLoadingTrips(true);
    setLoadingFooter(true);
    setLoadingFooterTxt('Carregando viagens...');

    const response =
      await emitAsync(
        socket,
        'tripsByRange:get',
        {
          requestId,
          startDate: start,
          endDate: end,
          linhasParaCarregar
        }
      );

    if (!response?.ok) {

      finishTripsRequest(requestId);

      return false;
    }

    return true;

  } catch (err) {

    finishTripsRequest(requestId);

    throw err;
  }
}

export async function loadTripAnalysis(
  socket,
  {
    date,
    codigo,
    types
  }
) {

  if (
    !socket?.connected ||
    !date ||
    !codigo ||
    !types?.length
  ) {
    return null;
  }

  const response =
    await emitAsync(
      socket,
      'tripAnalysis:get',
      {
        date,
        codigo,
        types
      }
    );

  if (!response?.ok) {
    return null;
  }

  return response.analysis || null;
}